import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send } from "lucide-react";
import API from "@/lib/api";
import { FadeUp, MaskedLine } from "@/components/Reveal";

const SPREAD_OPTIONS = [
  "Single card pull",
  "Three-card story",
  "Celtic cross deep dive",
];

const TAROT_IMG =
  "https://images.pexels.com/photos/14190188/pexels-photo-14190188.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const inputCls =
  "w-full rounded-lg border border-line bg-surface px-4 py-3.5 text-sm text-paper placeholder:text-mist/50 outline-none transition-colors duration-300 focus:border-gold/60";

export default function Tarot() {
  const [form, setForm] = useState({ name: "", email: "", spread: SPREAD_OPTIONS[1], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await API.post("/readings", form);
      toast.success("Your request is in the cards — I'll be in touch to schedule.", {
        description: `Spread: ${form.spread}`,
      });
      setForm({ name: "", email: "", spread: SPREAD_OPTIONS[1], message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "The cards slipped — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-16" data-testid="tarot-page">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-start">
        <div>
          <FadeUp>
            <p className="text-[12px] uppercase tracking-widest2 text-gold">The Reading Room</p>
            <h1 className="mt-4 font-display font-light tracking-tighter text-[clamp(2.8rem,6vw,5.5rem)] leading-[0.98]">
              <MaskedLine delay={0.1}>What does</MaskedLine>
              <MaskedLine delay={0.25}>
                <span className="italic text-gold">the deck</span> say?
              </MaskedLine>
            </h1>
            <p className="mt-8 text-mist leading-relaxed max-w-md">
              Tell me what&rsquo;s on your mind and pick a spread. I&rsquo;ll reply personally
              to schedule your reading — held in person or over a call, with the deck
              you see on the shelves.
            </p>
            <p className="mt-5 text-sm text-mist/80">
              Prefer to write directly?{" "}
              <a
                href="mailto:moonspiritvida@gmail.com"
                data-testid="tarot-contact-email"
                className="text-gold border-b border-gold/40 pb-0.5 transition-colors duration-300 hover:text-paper"
              >
                moonspiritvida@gmail.com
              </a>
            </p>
          </FadeUp>

          <FadeUp delay={0.15} className="mt-10">
            <div className="spotlight">
              <div className="overflow-hidden rounded-xl border border-line">
                <img
                  src={TAROT_IMG}
                  alt="Tarot cards on ritual cloth"
                  data-testid="tarot-page-image"
                  className="w-full h-[300px] md:h-[380px] object-cover"
                />
              </div>
            </div>
            <p className="mt-4 text-[11px] uppercase tracking-widest2 text-mist/50">
              Every reading closes with one card to carry through the week.
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.2}>
          <form
            onSubmit={submit}
            data-testid="tarot-booking-form"
            className="rounded-2xl border border-line bg-coal/70 p-8 md:p-10 space-y-6"
          >
            <p className="font-display text-2xl text-paper">Request a reading</p>

            <div>
              <label htmlFor="reading-name" className="text-[11px] uppercase tracking-widest2 text-mist">
                Your name
              </label>
              <input
                id="reading-name"
                data-testid="reading-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="What shall I call you?"
                className={`mt-2 ${inputCls}`}
              />
            </div>

            <div>
              <label htmlFor="reading-email" className="text-[11px] uppercase tracking-widest2 text-mist">
                Email
              </label>
              <input
                id="reading-email"
                data-testid="reading-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@somewhere.quiet"
                className={`mt-2 ${inputCls}`}
              />
            </div>

            <div>
              <label htmlFor="reading-spread" className="text-[11px] uppercase tracking-widest2 text-mist">
                Choose a spread
              </label>
              <select
                id="reading-spread"
                data-testid="reading-spread-select"
                value={form.spread}
                onChange={set("spread")}
                className={`mt-2 ${inputCls}`}
              >
                {SPREAD_OPTIONS.map((s) => (
                  <option key={s} value={s} className="bg-surface">
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="reading-message" className="text-[11px] uppercase tracking-widest2 text-mist">
                Your question or area of focus
              </label>
              <textarea
                id="reading-message"
                data-testid="reading-message-textarea"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="It can be as plain or as tangled as it really is."
                className={`mt-2 resize-none ${inputCls}`}
              />
            </div>

            <motion.button
              whileTap={{ scale: 0.97 }}
              type="submit"
              disabled={sending}
              data-testid="reading-submit-button"
              className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-widest2 text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60"
            >
              {sending ? "Shuffling the deck…" : "Send my request"}
              <Send size={15} />
            </motion.button>
          </form>
        </FadeUp>
      </section>
    </main>
  );
}
